import { apparentTemperatureC, legacyHeatAdvisory } from './apparent-temperature';

export type WeatherSource = 'device-location' | 'active-site';
export type WeatherSnapshot = {
  temperatureC: number;
  relativeHumidityPct: number;
  feelsLikeC: number | null;
  condition: string;
  weatherCode?: number | null;
  isDay?: boolean | null;
  risk: string | null;
  observedAt: string;
  source: WeatherSource;
  /** Who reported the observation. Carried so a record can name its origin. */
  provider: string;
};
export type WeatherQuery = { source: WeatherSource; latitude?: number; longitude?: number };

export interface WeatherAdapter {
  current(query: WeatherQuery): Promise<WeatherSnapshot | null>;
}

/**
 * Read-only provider. Missing provider configuration and failures return no observation.
 *
 * Retained for a future server-side relay only. It is not wired to any screen: Hosting rewrites
 * `**` to index.html, so `/api/weather/current` answers with HTML and every call fails closed.
 */
export class HttpWeatherAdapter implements WeatherAdapter {
  constructor(
    private readonly endpoint = '/api/weather/current',
    private readonly request: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  async current(query: WeatherQuery): Promise<WeatherSnapshot | null> {
    const params = new URLSearchParams({ source: query.source });
    if (query.latitude !== undefined) params.set('latitude', String(query.latitude));
    if (query.longitude !== undefined) params.set('longitude', String(query.longitude));
    try {
      const response = await this.request(`${this.endpoint}?${params.toString()}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const value = (await response.json()) as Partial<WeatherSnapshot>;
      if (
        typeof value.temperatureC !== 'number' ||
        typeof value.relativeHumidityPct !== 'number' ||
        typeof value.provider !== 'string' ||
        (value.feelsLikeC !== null && typeof value.feelsLikeC !== 'number') ||
        typeof value.condition !== 'string' ||
        typeof value.observedAt !== 'string' ||
        (value.source !== 'device-location' && value.source !== 'active-site') ||
        (value.risk !== null && typeof value.risk !== 'string')
      ) {
        return null;
      }
      return value as WeatherSnapshot;
    } catch {
      return null;
    }
  }
}

/**
 * WMO weather interpretation codes as Open-Meteo publishes them. Only codes the provider actually
 * documents are mapped; anything else falls back to the reported humidity so the panel never
 * invents a sky condition it was not told about.
 */
const WMO_CONDITIONS: Readonly<Record<number, string>> = {
  0: '맑음',
  1: '대체로 맑음',
  2: '구름 조금',
  3: '흐림',
  45: '안개',
  48: '착빙 안개',
  51: '약한 이슬비',
  53: '이슬비',
  55: '강한 이슬비',
  56: '어는 이슬비',
  57: '강한 어는 이슬비',
  61: '약한 비',
  63: '비',
  65: '강한 비',
  66: '어는 비',
  67: '강한 어는 비',
  71: '약한 눈',
  73: '눈',
  75: '강한 눈',
  77: '싸락눈',
  80: '약한 소나기',
  81: '소나기',
  82: '강한 소나기',
  85: '약한 소낙눈',
  86: '소낙눈',
  95: '뇌우',
  96: '우박 동반 뇌우',
  99: '강한 우박 동반 뇌우',
};

const OPEN_METEO_ENDPOINT = 'https://api.open-meteo.com/v1/forecast';
export const OPEN_METEO_PROVIDER = 'open-meteo-current';
const KST_LOCAL_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/u;
const round1 = (value: number) => Math.round(value * 10) / 10;

/**
 * Current conditions from Open-Meteo.
 *
 * The legacy dashboard recorded why this provider and not 기상청: data.go.kr answers browser
 * (Origin) requests with 403, so the client cannot call it at all. Open-Meteo is free, needs no API
 * key and allows CORS, so it is called directly from the browser with no server hop. A site stored
 * as 기상청 nx/ny is converted to lat/lon before it reaches this adapter.
 *
 * Fail-closed: an unusable response, an unparseable observation time or a missing coordinate all
 * return `null`, so a screen shows no weather rather than a fabricated one.
 */
export class OpenMeteoWeatherAdapter implements WeatherAdapter {
  constructor(
    private readonly endpoint = OPEN_METEO_ENDPOINT,
    private readonly request: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  async current(query: WeatherQuery): Promise<WeatherSnapshot | null> {
    const { latitude, longitude } = query;
    if (
      typeof latitude !== 'number' ||
      typeof longitude !== 'number' ||
      !Number.isFinite(latitude) ||
      !Number.isFinite(longitude) ||
      Math.abs(latitude) > 90 ||
      Math.abs(longitude) > 180
    )
      return null;
    const params = new URLSearchParams({
      latitude: latitude.toFixed(4),
      longitude: longitude.toFixed(4),
      current: 'temperature_2m,relative_humidity_2m,weather_code,is_day',
      timezone: 'Asia/Seoul',
    });
    try {
      const response = await this.request(`${this.endpoint}?${params.toString()}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const body = (await response.json()) as { current?: Record<string, unknown> } | null;
      const current = body?.current;
      if (!current || typeof current !== 'object') return null;
      const temperatureC = Number(current.temperature_2m);
      const relativeHumidityPct = Number(current.relative_humidity_2m);
      if (
        !Number.isFinite(temperatureC) ||
        temperatureC < -60 ||
        temperatureC > 60 ||
        !Number.isFinite(relativeHumidityPct) ||
        relativeHumidityPct < 0 ||
        relativeHumidityPct > 100
      )
        return null;
      const localTime = String(current.time ?? '');
      if (!KST_LOCAL_TIME.test(localTime)) return null;
      const observedAt = `${localTime}:00+09:00`;
      if (!Number.isFinite(Date.parse(observedAt))) return null;
      const code = typeof current.weather_code === 'number' ? current.weather_code : NaN;
      const condition =
        Number.isInteger(code) && WMO_CONDITIONS[code]
          ? WMO_CONDITIONS[code]
          : `습도 ${Math.round(relativeHumidityPct)}%`;
      const feels = apparentTemperatureC(temperatureC, relativeHumidityPct);
      const advisory = feels === null ? null : legacyHeatAdvisory(feels);
      return {
        temperatureC: round1(temperatureC),
        relativeHumidityPct: round1(relativeHumidityPct),
        feelsLikeC: feels === null ? null : round1(feels),
        condition,
        weatherCode: Number.isInteger(code) && WMO_CONDITIONS[code] ? code : null,
        isDay: current.is_day === 1 ? true : current.is_day === 0 ? false : null,
        risk:
          advisory && advisory.stage !== '정상' && feels !== null
            ? `체감 ${round1(feels).toFixed(1)}°C · ${advisory.stage} · ${advisory.action}`
            : null,
        observedAt,
        source: query.source,
        provider: OPEN_METEO_PROVIDER,
      };
    } catch {
      return null;
    }
  }
}
